import {
  CommandGroup,
  CommandItem,
  CommandList,
  CommandInput,
} from "@/components/ui/command";
import { Command as CommandPrimitive } from "cmdk";
import { useState, useRef, useCallback, type KeyboardEvent } from "react";

import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export type Option = Record<"value" | "label", string> & Record<string, string>;

export interface Group {
  groupName: string;
  options: Option[];
}

type AutoCompleteProps = {
  options: Option[] | Group[];
  emptyMessage: string;
  value?: Option;
  onValueChange?: (value: Option | undefined) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  invalid?: boolean;
  className?: string;
  allowEmptySelection?: boolean;
  inputClassName?: string;
  "data-testid"?: string;
};

export const AutoComplete = ({
  options,
  placeholder,
  emptyMessage,
  value,
  onValueChange,
  disabled,
  isLoading = false,
  invalid = false,
  className,
  allowEmptySelection = true,
  inputClassName,
  "data-testid": dataTestId,
}: AutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setOpen] = useState(false);
  const [selected, setSelected] = useState<Option | undefined>(value as Option);
  const [inputValue, setInputValue] = useState<string>(value?.label || "");
  const [
    isSelectedWithHandleSelectOption,
    setIsSelectedWithHandleSelectOption,
  ] = useState(false);

  const isGrouped = (options: Option[] | Group[]): options is Group[] => {
    return options.length > 0 && "groupName" in options[0];
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      if (!isOpen) {
        setOpen(true);
      }

      if (event.key === "Enter" && input.value !== "") {
        let optionToSelect: Option | undefined;

        if (isGrouped(options)) {
          for (const group of options) {
            optionToSelect = group.options.find(
              (option) =>
                option.label.toLowerCase() === input.value.toLowerCase()
            );
            if (optionToSelect) break;
          }
        } else {
          optionToSelect = options.find(
            (option) => option.label.toLowerCase() === input.value.toLowerCase()
          );
        }

        if (optionToSelect) {
          setSelected(optionToSelect);
          onValueChange?.(optionToSelect);
        }
      }

      if (event.key === "Escape") {
        input.blur();
      }
    },
    [isOpen, options, onValueChange]
  );

  const handleBlur = useCallback(() => {
    if (isSelectedWithHandleSelectOption) {
      setIsSelectedWithHandleSelectOption(false);
      return;
    }

    let item = selected;

    if (inputValue === "" && allowEmptySelection) {
      setOpen(false);
      setSelected(undefined);
      setInputValue("");
      onValueChange?.(undefined);
      setIsSelectedWithHandleSelectOption(false);
      return;
    }

    if (isGrouped(options)) {
      for (const group of options as Group[]) {
        const option = group.options
          .sort((a, b) => {
            return a.label.localeCompare(b.label);
          })
          .find((option) => {
            return option.label
              .toLowerCase()
              .includes(inputValue.toLowerCase());
          });

        if (option) {
          item = option;
          break;
        }
      }
    } else {
      const option = (options as Option[]).find((option) => {
        return option.label.includes(inputValue);
      });

      if (option) {
        item = option;
      }
    }

    setOpen(false);
    setSelected(item);
    setInputValue(item?.label || "");
    onValueChange?.(item);
  }, [
    allowEmptySelection,
    inputValue,
    isSelectedWithHandleSelectOption,
    onValueChange,
    options,
    selected,
  ]);

  const handleSelectOption = useCallback(
    (selectedOption: Option) => {
      setInputValue(selectedOption.label);
      setSelected(selectedOption);
      onValueChange?.(selectedOption);
      setIsSelectedWithHandleSelectOption(true);
      setTimeout(() => {
        inputRef?.current?.blur();
      }, 0);
      setOpen(false);
    },
    [onValueChange]
  );

  const renderOptions = () => {
    if (isGrouped(options)) {
      return options.map((group) => (
        <CommandGroup key={group.groupName} heading={group.groupName}>
          {group.options.map((option) => {
            const isSelected = selected?.value === option.value;
            return (
              <CommandItem
                key={option.value}
                value={option.label}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onSelect={() => handleSelectOption(option)}
                className={cn(
                  "flex w-full items-center gap-2",
                  !isSelected ? "pl-8" : null
                )}
              >
                {isSelected ? <Check className="w-4" /> : null}
                {option.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      ));
    }

    return (
      <CommandGroup>
        {options.map((option) => {
          const isSelected = selected?.value === option.value;
          return (
            <CommandItem
              key={option.value}
              value={option.label}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onSelect={() => handleSelectOption(option)}
              className={cn(
                "flex w-full items-center gap-2",
                !isSelected ? "pl-8" : null
              )}
            >
              {isSelected ? <Check className="w-4" /> : null}
              {option.label}
            </CommandItem>
          );
        })}
      </CommandGroup>
    );
  };

  return (
    <CommandPrimitive className={className} onKeyDown={handleKeyDown}>
      <div className="w-full">
        <CommandInput
          ref={inputRef}
          value={inputValue}
          onValueChange={isLoading ? undefined : setInputValue}
          onBlur={handleBlur}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn("text-base", inputClassName)}
          invalid={invalid}
          data-testid={dataTestId}
        />
      </div>
      <div className="relative mt-1">
        <div
          className={cn(
            "absolute top-0 z-10 w-full rounded-xl outline-none animate-in fade-in-0 zoom-in-95",
            isOpen ? "block" : "hidden"
          )}
        >
          <CommandList className="min-h-[50px] rounded-lg">
            {isLoading ? (
              <CommandPrimitive.Loading>
                <div className="py-4">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                </div>
              </CommandPrimitive.Loading>
            ) : null}
            {options.length > 0 && !isLoading ? renderOptions() : null}
            {!isLoading ? (
              <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                {emptyMessage}
              </CommandPrimitive.Empty>
            ) : null}
          </CommandList>
        </div>
      </div>
    </CommandPrimitive>
  );
};
